import {
    Badge,
    Button,
    Divider,
    Group,
    Loader,
    Modal,
    NumberInput,
    Select,
    Stack,
    Switch,
    Tabs,
    Text,
    Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { Trash } from '@phosphor-icons/react';
import { useEffect, useState, type FC } from 'react';

import { currencySymbol } from '@utils/currency';
import type { CapacityUnit, DestFillPolicy, RebalanceMerchantConfig, RebalanceNode, RebalanceWarehousePath, SourceSplitPolicy } from '@api/rebalance';
import { listWarehousesForStn, type WarehousePickItem } from '@api/warehouse-ops';
import { createRebalancePath, deleteRebalancePath, getRebalanceConfig, getRebalanceNodes, getRebalancePaths, updateRebalancePath, upsertRebalanceConfig, upsertRebalancePaths } from '@api/rebalance';
import RebalanceNodesTab from './RebalanceNodesTab';
import WarehouseDemandTab from './WarehouseDemandTab';

const UNASSIGNED = '__unassigned__';

interface Props {
    opened: boolean;
    onClose: () => void;
}

/** The message the API actually returned (NestJS bodies arrive as { error: { message } }), else a fallback. */
const apiErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as { error?: { message?: string | string[] }; message?: string } | null;
    const raw = e?.error?.message ?? e?.message;
    const text = Array.isArray(raw) ? raw.join('; ') : raw;
    return text && text.trim() ? text : fallback;
};

// ─── Routes tab ───────────────────────────────────────────────────────────────

const RoutesTab: FC = () => {
    const [paths, setPaths] = useState<RebalanceWarehousePath[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<{
        leadTimeDays: number | '';
        costPerUnit: number | '';
        isActive: boolean;
    }>({ leadTimeDays: '', costPerUnit: '', isActive: true });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Add-route form. Routes run between the merchant's INTERNAL warehouses (the rebalance tier);
    // channel FCs and vendor sites are distribution / procurement lanes, not rebalance routes.
    const [internalWarehouses, setInternalWarehouses] = useState<WarehousePickItem[]>([]);
    const [nodes, setNodes] = useState<RebalanceNode[]>([]);
    const [newFromNode, setNewFromNode] = useState<string | null>(null);
    const [newToNode, setNewToNode] = useState<string | null>(null);
    const [newFrom, setNewFrom] = useState<string | null>(null);
    const [newTo, setNewTo] = useState<string | null>(null);
    const [newLeadTime, setNewLeadTime] = useState<number | ''>('');
    const [newCost, setNewCost] = useState<number | ''>('');
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    useEffect(() => {
        getRebalancePaths()
            .then(setPaths)
            .catch(() => setPaths([]))
            .finally(() => setLoading(false));
        listWarehousesForStn()
            .then(ws => setInternalWarehouses(ws.filter(w => w.type === 'INTERNAL')))
            .catch(() => setInternalWarehouses([]));
        getRebalanceNodes()
            .then(r => setNodes(r.nodes.filter(n => n.isActive)))
            .catch(() => setNodes([]));
    }, []);

    const warehouseOptions = internalWarehouses
        .map(w => ({ value: w.warehouseId, label: w.name === w.warehouseId ? w.name : `${w.name} (${w.warehouseId})` }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // With nodes configured the picker is two-step: node first, then a member of that node.
    const hasNodes = nodes.length > 0;
    const nodeOfWarehouse = new Map<string, string>();
    nodes.forEach(n => n.members.forEach(m => nodeOfWarehouse.set(m.warehouseId, n.id)));
    const nodeOptions = [
        ...nodes.map(n => ({ value: n.id, label: n.name })),
        { value: UNASSIGNED, label: 'Not in a node' },
    ];
    const warehousesInNode = (nodeId: string | null) =>
        !nodeId ? warehouseOptions
            : nodeId === UNASSIGNED ? warehouseOptions.filter(o => !nodeOfWarehouse.has(o.value))
                : warehouseOptions.filter(o => nodeOfWarehouse.get(o.value) === nodeId);
    const nodeNameOf = (whId: string | null) => {
        if (!whId) return null;
        const nodeId = nodeOfWarehouse.get(whId);
        return nodeId ? nodes.find(n => n.id === nodeId)?.name ?? null : null;
    };

    const addRoute = async () => {
        if (!newFrom || !newTo) { setAddError('Pick both a source and a destination warehouse.'); return; }
        if (newFrom === newTo) { setAddError('Source and destination must be different warehouses.'); return; }
        if (newLeadTime === '') { setAddError('Lead time is required — the plan uses it to date arrivals.'); return; }
        if (paths.some(p => p.fromWarehouseId === newFrom && p.toWarehouseId === newTo)) {
            setAddError('That route already exists — edit it in the list below.');
            return;
        }
        setAdding(true);
        setAddError(null);
        try {
            const created = await createRebalancePath({
                fromWarehouseId: newFrom,
                toWarehouseId: newTo,
                leadTimeDays: Number(newLeadTime),
                costPerUnit: newCost === '' ? null : Number(newCost),
                isActive: true,
            });
            setPaths(prev => [...prev, created]);
            setNewFrom(null);
            setNewTo(null);
            setNewLeadTime('');
            setNewCost('');
        } catch (err: any) {
            setAddError(apiErrorMessage(err, 'Could not add the route. Please try again.'));
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (path: RebalanceWarehousePath) => {
        setEditingId(path.id);
        setEditValues({
            leadTimeDays: path.leadTimeDays ?? '',
            costPerUnit: path.costPerUnit ?? '',
            isActive: path.isActive,
        });
    };

    const saveEdit = async (id: string) => {
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateRebalancePath(id, {
                leadTimeDays: editValues.leadTimeDays === '' ? null : Number(editValues.leadTimeDays),
                costPerUnit: editValues.costPerUnit === '' ? null : Number(editValues.costPerUnit),
                isActive: editValues.isActive,
            });
            setPaths(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingId(null);
        } catch (err: any) {
            setSaveError(apiErrorMessage(err, 'Save failed. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    // Removes the row from the database (not the Active toggle, which only hides it from new plans).
    const confirmDelete = (route: RebalanceWarehousePath) => {
        modals.openConfirmModal({
            title: 'Delete route?',
            children: (
                <Text size="sm">
                    This removes the route <strong>{route.fromWarehouseId} → {route.toWarehouseId}</strong> from
                    the database. Plans already approved on it keep their transfers; new plans will not use it.
                </Text>
            ),
            labels: { confirm: 'Delete route', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setDeletingId(route.id);
                setDeleteError(null);
                try {
                    await deleteRebalancePath(route.id);
                    setPaths(prev => prev.filter(p => p.id !== route.id));
                    if (editingId === route.id) setEditingId(null);
                } catch (err: any) {
                    setDeleteError(apiErrorMessage(err, 'Delete failed. Please try again.'));
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    const routes = paths.filter(p => p.fromWarehouseId !== null);

    if (loading) return <Loader size="sm" />;

    return (
        <Stack gap="xl">
            <div>
                <Title order={6} mb="xs">Add a route</Title>
                <Text size="xs" c="dimmed" mb="sm">
                    A route is one direction only — add the reverse lane separately if stock can flow both ways.
                    Only warehouses on an active route take part in rebalancing.
                    {hasNodes && ' Routes stay warehouse to warehouse; a network run uses the ones that cross two nodes, a within-node run the ones inside it.'}
                </Text>
                {addError && <Text size="xs" c="red" mb="xs">{addError}</Text>}
                <Group gap="xs" align="flex-end" wrap="wrap">
                    {hasNodes && (
                        <Select
                            label="From node"
                            placeholder="Any"
                            size="xs"
                            w={160}
                            clearable
                            data={nodeOptions}
                            value={newFromNode}
                            onChange={v => { setNewFromNode(v); setNewFrom(null); }}
                        />
                    )}
                    <Select
                        label="From warehouse"
                        placeholder="Source"
                        size="xs"
                        w={240}
                        searchable
                        data={warehousesInNode(newFromNode).filter(o => o.value !== newTo)}
                        value={newFrom}
                        onChange={setNewFrom}
                        nothingFoundMessage={hasNodes && newFromNode ? 'No warehouse in that node' : 'No internal warehouses'}
                    />
                    {hasNodes && (
                        <Select
                            label="To node"
                            placeholder="Any"
                            size="xs"
                            w={160}
                            clearable
                            data={nodeOptions}
                            value={newToNode}
                            onChange={v => { setNewToNode(v); setNewTo(null); }}
                        />
                    )}
                    <Select
                        label="To warehouse"
                        placeholder="Destination"
                        size="xs"
                        w={240}
                        searchable
                        data={warehousesInNode(newToNode).filter(o => o.value !== newFrom)}
                        value={newTo}
                        onChange={setNewTo}
                        nothingFoundMessage={hasNodes && newToNode ? 'No warehouse in that node' : 'No internal warehouses'}
                    />
                    <NumberInput
                        label="Lead Time (days)"
                        size="xs"
                        value={newLeadTime}
                        onChange={v => setNewLeadTime(v === '' ? '' : Number(v))}
                        w={130}
                        min={0}
                        suffix="d"
                    />
                    <NumberInput
                        label={`Cost/Unit (${currencySymbol().trim()})`}
                        size="xs"
                        value={newCost}
                        onChange={v => setNewCost(v === '' ? '' : Number(v))}
                        w={130}
                        min={0}
                    />
                    <Button size="compact-sm" onClick={addRoute} loading={adding}>Add route</Button>
                </Group>
            </div>

            <div>
                <Title order={6} mb="sm">Transfer Routes</Title>
                {deleteError && <Text size="xs" c="red" mb="xs">{deleteError}</Text>}
                {routes.length === 0 ? (
                    <Text size="sm" c="dimmed">No transfer routes configured yet.</Text>
                ) : (
                    <Stack gap="xs">
                        {routes.map(route => (
                            <div key={route.id}
                                style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 8, padding: 12 }}>
                                <Group justify="space-between" mb={editingId === route.id ? 8 : 0}>
                                    <Group gap="xs">
                                        <Text size="sm" fw={500}>
                                            {route.fromWarehouseId} → {route.toWarehouseId}
                                        </Text>
                                        {hasNodes && (() => {
                                            const a = nodeNameOf(route.fromWarehouseId);
                                            const b = nodeNameOf(route.toWarehouseId);
                                            const crosses = a !== b;
                                            return (
                                                <Badge color={crosses ? 'indigo' : 'gray'} variant="light" size="xs">
                                                    {a ?? 'alone'} → {b ?? 'alone'}{crosses ? '' : ' · within node'}
                                                </Badge>
                                            );
                                        })()}
                                        <Badge
                                            color={route.isActive ? 'green' : 'gray'}
                                            variant="light"
                                            size="xs"
                                        >
                                            {route.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </Group>
                                    {editingId !== route.id && (
                                        <Group gap="sm">
                                            <Text size="xs" c="dimmed">
                                                {route.leadTimeDays != null ? `${route.leadTimeDays}d lead` : '—'}
                                                {route.costPerUnit != null ? ` · ${currencySymbol()}${route.costPerUnit}/unit` : ''}
                                            </Text>
                                            <Button size="compact-xs" variant="subtle" onClick={() => startEdit(route)}>Edit</Button>
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="red"
                                                leftSection={<Trash size={12} />}
                                                loading={deletingId === route.id}
                                                onClick={() => confirmDelete(route)}
                                            >
                                                Delete
                                            </Button>
                                        </Group>
                                    )}
                                </Group>

                                {editingId === route.id && (
                                    <Stack gap="xs">
                                        {saveError && <Text size="xs" c="red">{saveError}</Text>}
                                        <Group gap="xs" align="flex-end" wrap="wrap">
                                            <NumberInput
                                                label="Lead Time (days)"
                                                size="xs"
                                                value={editValues.leadTimeDays}
                                                onChange={v => setEditValues(e => ({ ...e, leadTimeDays: v === '' ? '' : Number(v) }))}
                                                w={130}
                                                min={0}
                                                suffix="d"
                                            />
                                            <NumberInput
                                                label={`Cost/Unit (${currencySymbol().trim()})`}
                                                size="xs"
                                                value={editValues.costPerUnit}
                                                onChange={v => setEditValues(e => ({ ...e, costPerUnit: v === '' ? '' : Number(v) }))}
                                                w={130}
                                                min={0}
                                            />
                                            <Switch
                                                label="Active"
                                                size="sm"
                                                checked={editValues.isActive}
                                                onChange={e => setEditValues(ev => ({ ...ev, isActive: e.currentTarget.checked }))}
                                            />
                                            <Button size="compact-sm" onClick={() => saveEdit(route.id)} loading={saving}>Save</Button>
                                            <Button size="compact-sm" variant="subtle" onClick={() => { setEditingId(null); setSaveError(null); }}>Cancel</Button>
                                        </Group>
                                    </Stack>
                                )}
                            </div>
                        ))}
                    </Stack>
                )}
            </div>
        </Stack>
    );
};

// ─── Calculation Settings tab ─────────────────────────────────────────────────

const CalculationSettingsTab: FC = () => {
    const [paths, setPaths] = useState<RebalanceWarehousePath[]>([]);
    const [config, setConfig] = useState<RebalanceMerchantConfig | null>(null);
    const [loading, setLoading] = useState(true);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<{ targetDoh: number | '' }>({ targetDoh: '' });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [orgDohValue, setOrgDohValue] = useState<number | ''>(30);
    const [bufferValue, setBufferValue] = useState<number | ''>(0);
    const [capacityUnit, setCapacityUnit] = useState<CapacityUnit | 'OFF'>('OFF');
    const [sourcePolicy, setSourcePolicy] = useState<SourceSplitPolicy>('RANK');
    const [destPolicy, setDestPolicy] = useState<DestFillPolicy>('RANK');
    // Baseline values — reflect what's persisted; used to detect unsaved changes
    const [savedOrgDoh, setSavedOrgDoh] = useState<number | ''>(30);
    const [savedBuffer, setSavedBuffer] = useState<number | ''>(0);
    const [savedCapacityUnit, setSavedCapacityUnit] = useState<CapacityUnit | 'OFF'>('OFF');
    const [savedSourcePolicy, setSavedSourcePolicy] = useState<SourceSplitPolicy>('RANK');
    const [savedDestPolicy, setSavedDestPolicy] = useState<DestFillPolicy>('RANK');
    const [savingCalc, setSavingCalc] = useState(false);
    const [calcError, setCalcError] = useState<string | null>(null);
    const [calcSaved, setCalcSaved] = useState(false);

    const isCalcDirty = orgDohValue !== savedOrgDoh || bufferValue !== savedBuffer
        || capacityUnit !== savedCapacityUnit || sourcePolicy !== savedSourcePolicy || destPolicy !== savedDestPolicy;

    useEffect(() => {
        Promise.all([getRebalancePaths(), getRebalanceConfig()])
            .then(([p, c]) => {
                setPaths(p);
                setConfig(c);
                const doh = c?.defaultTargetDoh ?? 30;
                const buf = c?.inTransitBufferDays ?? 0;
                setOrgDohValue(doh);
                setBufferValue(buf);
                setSavedOrgDoh(doh);
                setSavedBuffer(buf);
                const cap = c?.capacityUnit ?? 'OFF';
                setCapacityUnit(cap);
                setSavedCapacityUnit(cap);
                const sp = c?.defaultSourceSplitPolicy ?? 'RANK';
                const dp = c?.defaultDestFillPolicy ?? 'RANK';
                setSourcePolicy(sp); setSavedSourcePolicy(sp);
                setDestPolicy(dp); setSavedDestPolicy(dp);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const routes = paths.filter(p => p.fromWarehouseId !== null);
    const whConfigs = paths.filter(p => p.fromWarehouseId === null);
    const allWarehouseIds = [...new Set(routes.flatMap(r => [r.fromWarehouseId!, r.toWarehouseId]))].sort();
    const whConfigByWh = new Map(whConfigs.map(c => [c.toWarehouseId, c]));

    const saveCalcSettings = async () => {
        setSavingCalc(true);
        setCalcError(null);
        setCalcSaved(false);
        try {
            const doh = orgDohValue === '' ? 30 : Number(orgDohValue);
            const buf = bufferValue === '' ? 0 : Number(bufferValue);
            const updated = await upsertRebalanceConfig({
                scoringWeights: config?.scoringWeights ?? { deficitUrgency: 0.5, costEfficiency: 0.3, speed: 0.2 },
                warehousePriorityOrder: config?.warehousePriorityOrder ?? null,
                defaultTargetDoh: doh,
                inTransitBufferDays: buf,
                capacityUnit: capacityUnit === 'OFF' ? null : capacityUnit,
                defaultSourceSplitPolicy: sourcePolicy,
                defaultDestFillPolicy: destPolicy,
            });
            setConfig(updated);
            setSavedOrgDoh(doh);
            setSavedBuffer(buf);
            setSavedCapacityUnit(capacityUnit);
            setSavedSourcePolicy(sourcePolicy);
            setSavedDestPolicy(destPolicy);
            setCalcSaved(true);
            setTimeout(() => setCalcSaved(false), 2000);
        } catch (err: any) {
            setCalcError(err?.message ?? 'Save failed. Please try again.');
        } finally {
            setSavingCalc(false);
        }
    };

    const saveWhTargetDoh = async (warehouseId: string) => {
        setSaving(true);
        setSaveError(null);
        const existing = whConfigByWh.get(warehouseId);
        const targetDohVal = editValues.targetDoh === '' ? null : Number(editValues.targetDoh);
        try {
            if (existing) {
                const updated = await updateRebalancePath(existing.id, { targetDoh: targetDohVal });
                setPaths(prev => prev.map(p => p.id === updated.id ? updated : p));
            } else {
                const updated = await upsertRebalancePaths([{ fromWarehouseId: null, toWarehouseId: warehouseId, targetDoh: targetDohVal }]);
                setPaths(prev => {
                    const kept = prev.filter(p => p.fromWarehouseId !== null || p.toWarehouseId !== warehouseId);
                    return [...kept, ...updated.filter(u => u.fromWarehouseId === null && u.toWarehouseId === warehouseId)];
                });
            }
            setEditingId(null);
        } catch (err: any) {
            setSaveError(err?.message ?? 'Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loader size="sm" />;

    return (
        <Stack gap="xl">
            {/* Org-level defaults */}
            <div>
                <Title order={6} mb="xs">Calculation Defaults</Title>
                <Text size="xs" c="dimmed" mb="md">
                    These apply to every rebalance run unless overridden per-run.
                </Text>
                <Stack gap="sm" maw={400}>
                    <NumberInput
                        label="Organisation default target DOH"
                        description="Applied to all warehouses unless overridden below."
                        size="sm"
                        value={orgDohValue}
                        onChange={v => setOrgDohValue(v === '' ? '' : Number(v))}
                        min={1}
                        suffix="d"
                    />
                    <div>
                        <NumberInput
                            label="In-transit look-ahead beyond lead time"
                            description="Extends the in-transit capture window by this many days past the route lead time. Demand window stays at lead time."
                            size="sm"
                            value={bufferValue}
                            onChange={v => setBufferValue(v === '' ? '' : Number(v))}
                            min={0}
                            max={30}
                            suffix="d"
                        />
                        {(bufferValue !== '' && Number(bufferValue) > 0) && (
                            <Text size="xs" c="dimmed" mt={4}>
                                In-transit POs arriving up to {Number(bufferValue)}d after the route lead time will be counted as available stock at the destination.
                            </Text>
                        )}
                    </div>
                    <Select
                        label="Warehouse capacity"
                        description="Reads max capacity from each warehouse's record. A warehouse with no capacity set is unlimited."
                        size="sm"
                        value={capacityUnit}
                        onChange={v => setCapacityUnit((v ?? 'OFF') as CapacityUnit | 'OFF')}
                        data={[
                            { value: 'OFF', label: 'Not enforced' },
                            { value: 'UNITS', label: 'Enforce, capacity is in units' },
                            { value: 'CBM', label: 'Enforce, capacity is in CBM (uses product volume)' },
                        ]}
                    />
                    <Select
                        label="Node default: which warehouse sends"
                        description="Used by nodes that do not set their own policy."
                        size="sm"
                        value={sourcePolicy}
                        onChange={v => setSourcePolicy((v ?? 'RANK') as SourceSplitPolicy)}
                        data={[
                            { value: 'RANK', label: 'By rank — drain the top-ranked warehouse to its floor first' },
                            { value: 'AVAILABILITY', label: 'By availability — the warehouse with the most surplus sends first' },
                        ]}
                    />
                    <Select
                        label="Node default: which warehouse receives"
                        size="sm"
                        value={destPolicy}
                        onChange={v => setDestPolicy((v ?? 'RANK') as DestFillPolicy)}
                        data={[
                            { value: 'RANK', label: 'By rank' },
                            { value: 'LARGEST_DEFICIT', label: 'By largest deficit' },
                        ]}
                    />
                    {calcError && <Text size="xs" c="red">{calcError}</Text>}
                    {isCalcDirty && (
                        <Button
                            size="sm"
                            variant="light"
                            color="violet"
                            w="fit-content"
                            loading={savingCalc}
                            onClick={saveCalcSettings}
                        >
                            Save
                        </Button>
                    )}
                    {calcSaved && !isCalcDirty && (
                        <Text size="xs" c="green">Saved</Text>
                    )}
                </Stack>
            </div>

            <Divider />

            {/* Per-warehouse DOH overrides */}
            <div>
                <Title order={6} mb="sm">Warehouse Target DOH Overrides</Title>
                <Text size="xs" c="dimmed" mb="md">
                    Override the target days-on-hand per warehouse. Leave blank to use the organisation default above.
                    In a network run this is also the floor a warehouse keeps when it sends on behalf of its node, unless the node sets a keep floor for it.
                </Text>
                {allWarehouseIds.length === 0 ? (
                    <Text size="sm" c="dimmed">No warehouses found. Add transfer routes in the Routes tab first.</Text>
                ) : (
                    <Stack gap="xs">
                        {allWarehouseIds.map(whId => {
                            const whConfig = whConfigByWh.get(whId);
                            const editKey = `doh-${whId}`;
                            return (
                                <Group key={whId} justify="space-between" p="xs"
                                    style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 8 }}>
                                    <Text size="sm" fw={500}>{whId}</Text>
                                    {editingId === editKey ? (
                                        <Stack gap={4} align="flex-end">
                                            {saveError && <Text size="xs" c="red">{saveError}</Text>}
                                            <Group gap="xs">
                                                <NumberInput
                                                    size="xs"
                                                    placeholder="Target DOH"
                                                    value={editValues.targetDoh}
                                                    onChange={v => setEditValues(e => ({ ...e, targetDoh: v === '' ? '' : Number(v) }))}
                                                    w={100}
                                                    min={1}
                                                    suffix="d"
                                                />
                                                <Button size="compact-xs" onClick={() => saveWhTargetDoh(whId)} loading={saving}>Save</Button>
                                                <Button size="compact-xs" variant="subtle" onClick={() => { setEditingId(null); setSaveError(null); }}>Cancel</Button>
                                            </Group>
                                        </Stack>
                                    ) : (
                                        <Group gap="xs">
                                            <Text size="sm" c={whConfig?.targetDoh ? undefined : 'dimmed'}>
                                                {whConfig?.targetDoh ? `${whConfig.targetDoh}d` : 'org default'}
                                            </Text>
                                            <Button size="compact-xs" variant="subtle" onClick={() => {
                                                setEditingId(editKey);
                                                setEditValues({ targetDoh: whConfig?.targetDoh ?? '' });
                                            }}>Edit</Button>
                                        </Group>
                                    )}
                                </Group>
                            );
                        })}
                    </Stack>
                )}
            </div>
        </Stack>
    );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

const RebalanceSettingsModal: FC<Props> = ({ opened, onClose }) => {
    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600} size="md">Rebalance Settings</Text>}
            size="90%"
            radius="md"
        >
            <Tabs defaultValue="nodes" color="#6366f1">
                <Tabs.List mb="md">
                    <Tabs.Tab value="nodes">Nodes</Tabs.Tab>
                    <Tabs.Tab value="routes">Routes</Tabs.Tab>
                    <Tabs.Tab value="warehouse-demand">Warehouse Demand</Tabs.Tab>
                    <Tabs.Tab value="calculation">Calculation Settings</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="nodes">
                    <RebalanceNodesTab />
                </Tabs.Panel>

                <Tabs.Panel value="routes">
                    <RoutesTab />
                </Tabs.Panel>

                <Tabs.Panel value="warehouse-demand">
                    <WarehouseDemandTab />
                </Tabs.Panel>

                <Tabs.Panel value="calculation">
                    <CalculationSettingsTab />
                </Tabs.Panel>
            </Tabs>
        </Modal>
    );
};

export default RebalanceSettingsModal;
